import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState<'idle' | 'ok' | 'error' | 'pending' | 'need-auth'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) setStatus('error');
  }, [token]);

  async function accept() {
    if (!token) return;
    setStatus('pending');
    setError(null);
    try {
      await api<{ ok: true }>('/api/invites/accept', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      setStatus('ok');
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 401) {
        setStatus('need-auth');
        return;
      }
      setStatus('error');
      setError(e.message ?? 'accept failed');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Accept invitation</CardTitle>
          <CardDescription>
            You&apos;ve been invited to join a Pure organisation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!token && (
            <Alert variant="destructive">
              <AlertDescription>No invite token in URL.</AlertDescription>
            </Alert>
          )}
          {status === 'ok' && (
            <Alert variant="success">
              <AlertDescription>You&apos;ve joined the org.</AlertDescription>
            </Alert>
          )}
          {status === 'need-auth' && (
            <Alert>
              <AlertDescription>
                Please <Link to={`/login?redirect=${encodeURIComponent(window.location.href)}`} className="underline">sign in</Link> or{' '}
                <Link to={`/signup?redirect=${encodeURIComponent(window.location.href)}`} className="underline">create an account</Link>{' '}
                first, then accept the invite.
              </AlertDescription>
            </Alert>
          )}
          {status === 'error' && error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {token && status !== 'ok' && status !== 'need-auth' && (
            <Button onClick={accept} disabled={status === 'pending'}>
              {status === 'pending' ? 'Accepting…' : 'Accept invitation'}
            </Button>
          )}
          <div>
            <Link to="/dashboard" className="text-sm underline-offset-4 hover:underline">
              Go to dashboard →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
